import { useQuery } from "@tanstack/react-query";
import { useAuth, useToasts } from "../contexts";
import { getErrorWithMessage } from "../helper-functions";
import { GenericQueryOptions, QueryResult } from "../models";
import { useCallback } from "react";

export const useGenericQuery = <TData, TError>(
    options: GenericQueryOptions<TData, TError>
): QueryResult<TData, TError> => {
    const { isRefreshing } = useAuth();
    const { queryFn, queryKey, enabled, disableErrorToast, errorToast, onError, ...rest } = options;

    const { showToast } = useToasts();

    const _onError = useCallback(
        (error: TError) => {
            if (onError) {
                return onError(error);
            }
            if (disableErrorToast === true) return;
            const { message } = getErrorWithMessage(error);
            showToast(
                errorToast ?? {
                    name: "QUERY_ERROR",
                    titleInHeader: "Error",
                    body: "Your request was unsuccessful.\n" + message,
                    theme: "danger",
                    delay: 6000,
                }
            );
        },
        [onError, disableErrorToast, showToast, errorToast]
    );

    const { data, error, isError, isFetching, isLoading, isSuccess } = useQuery(
        queryKey,
        ({ signal }) => queryFn({ signal }),
        {
            onError: _onError,
            enabled: !isRefreshing && (enabled ?? false),
            keepPreviousData: true,
            ...rest,
        }
    );

    return { data, error, isError, isFetching, isLoading, isSuccess };
};
